import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight, Share2, Check } from "lucide-react";

export const Lightbox = ({ images, start = 0, alt = "Product photo", shareUrl, credit, onClose }) => {
  const [idx, setIdx] = useState(Math.max(0, Math.min(start, images.length - 1)));
  const [shared, setShared] = useState(false);
  const touchX = useRef(null);
  const many = images.length > 1;

  const go = useCallback((d) => setIdx((i) => (i + d + images.length) % images.length), [images.length]);

  const share = async (e) => {
    e.stopPropagation();
    const title = alt;
    const text = `${alt} — Kapatia Pvt Ltd`;
    const url = shareUrl || window.location.href;
    try {
      const res = await fetch(images[idx]);
      const blob = await res.blob();
      const file = new File([blob], "kapatia-product.jpg", { type: blob.type || "image/jpeg" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title, text });
        return;
      }
    } catch (err) {
      console.debug("Lightbox file share unavailable, falling back to URL share", err);
    }
    try {
      if (navigator.share) {
        await navigator.share({ title, text, url });
        return;
      }
    } catch (err) {
      if (err && err.name === "AbortError") return;
    }
    try {
      await navigator.clipboard.writeText(`${text} ${url}`);
      setShared(true);
      setTimeout(() => setShared(false), 2000);
    } catch (err) {
      console.debug("Lightbox clipboard copy failed", err);
    }
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft" && many) go(-1);
      else if (e.key === "ArrowRight" && many) go(1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [go, many, onClose]);

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="lightbox-overlay"
         role="dialog" aria-modal="true" aria-label={alt}
         style={{ background: "rgba(255,253,247,0.96)", backdropFilter: "blur(4px)" }}
         onClick={onClose}
         onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
         onTouchEnd={(e) => {
           if (touchX.current === null || !many) return;
           const dx = e.changedTouches[0].clientX - touchX.current;
           if (dx > 50) go(-1); else if (dx < -50) go(1);
           touchX.current = null;
         }}>
      <button onClick={onClose} aria-label="Close" data-testid="lightbox-close" autoFocus
              className="absolute right-3 top-3 z-10 grid h-11 w-11 place-items-center rounded-full transition-transform hover:scale-105 sm:right-5 sm:top-5"
              style={{ background: "#fff", color: "var(--green-950)", boxShadow: "0 4px 16px rgba(31,74,56,0.18)" }}>
        <X size={22} />
      </button>
      <button onClick={share} aria-label="Share photo" data-testid="lightbox-share"
              className="absolute right-16 top-3 z-10 grid h-11 w-11 place-items-center rounded-full transition-transform hover:scale-105 sm:right-20 sm:top-5"
              style={{ background: "#fff", color: shared ? "var(--green-700)" : "var(--green-950)", boxShadow: "0 4px 16px rgba(31,74,56,0.18)" }}>
        {shared ? <Check size={20} /> : <Share2 size={20} />}
      </button>
      {shared && (
        <span className="absolute right-3 top-16 z-10 rounded-full px-3.5 py-1.5 font-heading text-[12.5px] font-bold sm:right-5 sm:top-[76px]"
              data-testid="lightbox-share-copied"
              style={{ background: "var(--green-800)", color: "#fff", boxShadow: "0 4px 16px rgba(31,74,56,0.25)" }}>
          Link copied — paste to share
        </span>
      )}
      {many && (
        <>
          <button onClick={(e) => { e.stopPropagation(); go(-1); }} aria-label="Previous image" data-testid="lightbox-prev"
                  className="absolute left-2 top-1/2 z-10 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full transition-transform hover:scale-105 sm:left-5 sm:h-12 sm:w-12"
                  style={{ background: "#fff", color: "var(--green-950)", boxShadow: "0 4px 16px rgba(31,74,56,0.18)" }}>
            <ChevronLeft size={26} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); go(1); }} aria-label="Next image" data-testid="lightbox-next"
                  className="absolute right-2 top-1/2 z-10 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full transition-transform hover:scale-105 sm:right-5 sm:h-12 sm:w-12"
                  style={{ background: "#fff", color: "var(--green-950)", boxShadow: "0 4px 16px rgba(31,74,56,0.18)" }}>
            <ChevronRight size={26} />
          </button>
        </>
      )}
      <img src={images[idx]} alt={`${alt} ${idx + 1}`} data-testid="lightbox-image"
           onClick={(e) => e.stopPropagation()}
           className="max-h-[86vh] max-w-[94vw] select-none rounded-xl bg-white object-contain"
           style={{ boxShadow: "0 18px 50px rgba(31,74,56,0.22)" }} />
      {many && (
        <span className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full px-3.5 py-1 font-heading text-[12.5px] font-bold"
              data-testid="lightbox-counter" style={{ background: "#fff", color: "var(--green-950)", boxShadow: "0 4px 16px rgba(31,74,56,0.18)" }}>
          {idx + 1} / {images.length}
        </span>
      )}
      {credit && (
        <div data-testid="lightbox-credit" onClick={(e) => e.stopPropagation()}
             className="absolute bottom-3 left-3 max-w-[74vw] rounded-lg px-3 py-2 text-left"
             style={{ background: "rgba(13,58,43,0.88)", color: "#fff" }}>
          {credit.reference && <div className="text-[11px] font-bold">Model reference image — actual unit photos pending</div>}
          {credit.text && (
            <div className="mt-0.5 text-[10.5px]" style={{ color: "rgba(255,255,255,0.9)" }}>
              Image:{" "}
              {credit.source
                ? <a href={credit.source} target="_blank" rel="noopener noreferrer nofollow" className="underline" data-testid="lightbox-credit-source">{credit.text}</a>
                : credit.text}
              {credit.license && credit.licenseUrl && (
                <> · <a href={credit.licenseUrl} target="_blank" rel="license noopener noreferrer nofollow" className="underline" data-testid="lightbox-credit-license">{credit.license}</a></>
              )}
              {credit.changes && <span style={{ opacity: 0.8 }}> · {credit.changes}</span>}
            </div>
          )}
        </div>
      )}
    </div>,
    document.body
  );
};
